/**
 * An app's versions, where its install already lives (RFC 0313 3m, canvas 8-10).
 *
 * The Installed row's line says what everyone runs and whether the app has
 * changed since, with Install as the visible next click. Opening Versions shows
 * the list in the same modal, in the modal's own style: no cards, no accent
 * colour, Roll back on hover. Preview edits lets the author run the app as
 * edited in this window only; everyone else keeps the installed version.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { Automerge as A } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  changedSinceLive,
  type InstallResult,
  installApp,
  projectString,
  readVersions,
  rollbackApp,
  type Version,
} from '@mod/core/model'
import { ArrowLeft } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  setPreviewEdits,
  usePreviewEdits,
} from '../../services/apps/liveVersion'
import { readLocalPerson } from '../../services/identity/localPerson'
import {
  useDocument,
  useDocuments,
} from '../../services/world/repo/useDocument'

/** The app's versions, its live one, and how many of its documents moved. */
function useAppVersions(projectId: string, appId: string) {
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const versionsId = projectString(
    (projectDoc as { versionsDocId?: unknown } | undefined)?.versionsDocId,
  )
  const [versionsDoc] = useDocument<unknown>(
    (versionsId ?? undefined) as AnyDocumentId | undefined,
  )
  const [appDoc] = useDocument<{ live?: string }>(appId as AnyDocumentId)
  const versions = useMemo(
    () =>
      readVersions(versionsDoc).filter(
        (v) => v.covers.kind === 'app' && v.covers.appId === appId,
      ),
    [versionsDoc, appId],
  )
  const live = versions.find((v) => v.id === appDoc?.live)
  const ids = useMemo(
    () => Object.keys(live?.heads ?? {}) as AnyDocumentId[],
    [live],
  )
  const docs = useDocuments<unknown>(ids)
  const changed = useMemo(() => {
    if (!live) return 0
    const heads: Record<string, string[]> = {}
    for (const [id, d] of docs) heads[id] = A.getHeads(d as A.Doc<unknown>)
    return changedSinceLive(live, heads)
  }, [live, docs])
  return { versions, live, changed }
}

/** What an Installed row says, and whether Install is the next click. Pure. */
export function versionLine(
  live: Version | undefined,
  changed: number,
): { line: string | null; install: boolean } {
  if (!live) return { line: null, install: true }
  if (changed === 0) return { line: live.name, install: false }
  return {
    line: `${live.name} · ${changed} change${changed === 1 ? '' : 's'} not installed`,
    install: true,
  }
}

function useInstall(projectId: string, appId: string) {
  const repo = useRepo() as unknown as Repo
  const [refusal, setRefusal] = useState<InstallResult | null>(null)
  const [busy, setBusy] = useState(false)
  const run = (f: () => Promise<InstallResult>) => {
    setBusy(true)
    void f()
      .then((out) => setRefusal(out.ok ? null : out))
      .finally(() => setBusy(false))
  }
  return {
    busy,
    refusal,
    install: () =>
      run(() =>
        installApp(repo, projectId, appId, readLocalPerson()?.id ?? 'user'),
      ),
    rollback: (versionId: string) =>
      run(() => rollbackApp(repo, projectId, appId, versionId)),
    dismiss: () => setRefusal(null),
  }
}

/**
 * The Installed row's line, and Install when it is the next click. A hook, so
 * the row draws its own Install button beside its name rather than inside it.
 */
export function useAppVersionRow(projectId: string, appId: string) {
  const { live, changed } = useAppVersions(projectId, appId)
  const { line, install } = versionLine(live, changed)
  const act = useInstall(projectId, appId)
  return {
    line,
    // Only once there is a live version: an app that has never been installed
    // offers Install from its Versions page, where it says what that does.
    onInstall: install && live && !act.busy ? act.install : undefined,
  }
}

export function AppVersionsView({
  projectId,
  appId,
  name,
  onBack,
}: {
  projectId: string
  appId: string
  name: string
  onBack: () => void
}) {
  const { versions, live, changed } = useAppVersions(projectId, appId)
  const act = useInstall(projectId, appId)
  const previewing = usePreviewEdits(appId)
  const refusal =
    act.refusal && !act.refusal.ok && act.refusal.reason === 'breaks-records'
      ? act.refusal
      : null
  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 self-start text-muted-foreground text-xs hover:text-foreground"
      >
        <ArrowLeft size={12} /> Packages
      </button>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-foreground text-sm">{name}</div>
          <div className="text-muted-foreground text-xs">
            {live
              ? `Everyone in this project runs ${live.name}.`
              : 'Runs as edited. Install saves it as v1 and makes it what everyone runs.'}
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-muted-foreground text-xs">
          <input
            type="checkbox"
            checked={previewing}
            onChange={(e) => setPreviewEdits(appId, e.target.checked)}
          />
          Preview edits
        </label>
        <button
          type="button"
          disabled={act.busy || (!!live && changed === 0)}
          onClick={act.install}
          className="rounded-full bg-foreground px-3 py-1 font-medium text-background text-xs disabled:opacity-40"
        >
          Install as v{versions.length + 1}
        </button>
      </div>
      {refusal ? (
        <div className="rounded-2xl border border-border p-4 text-xs">
          <div className="mb-2 font-semibold text-sm">
            That version cannot draw every record
          </div>
          <p className="mb-2 text-foreground/80">
            {refusal.records} record{refusal.records === 1 ? '' : 's'} use a
            field it does not have.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={act.dismiss}
              className="rounded-full border border-border px-3 py-1"
            >
              Cancel
            </button>
            {refusal.suggest ? (
              <button
                type="button"
                onClick={() => act.rollback(refusal.suggest?.id ?? '')}
                className="rounded-full bg-foreground px-3 py-1 text-background"
              >
                Roll back to {refusal.suggest.name}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {live && changed > 0 ? (
        <div>
          <div className="mb-1 font-semibold text-foreground text-sm">
            Not installed
          </div>
          <div className="text-muted-foreground text-xs">
            {changed} document{changed === 1 ? '' : 's'} changed since{' '}
            {live.name}
          </div>
        </div>
      ) : null}
      <div>
        <div className="mb-1 font-semibold text-foreground text-sm">
          Versions
        </div>
        {versions.length === 0 ? (
          <div className="text-muted-foreground text-xs">No versions yet.</div>
        ) : null}
        {versions.map((v) => (
          <div
            key={v.id}
            className="group/ver -mx-2 flex items-center gap-3 rounded-lg px-2 py-1.5 text-xs hover:bg-muted"
          >
            <span className="w-8 font-semibold text-foreground">{v.name}</span>
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {v.id === live?.id ? 'Running · ' : ''}installed{' '}
              {new Date(v.at).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            {v.id !== live?.id ? (
              <button
                type="button"
                disabled={act.busy}
                onClick={() => act.rollback(v.id)}
                className="rounded-full border border-border px-2 opacity-0 group-hover/ver:opacity-100 disabled:opacity-40"
              >
                Roll back to {v.name}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
}
